import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import '../style.css';

const tabs = [
  { id: 'estudiante', label: '👨‍🎓 Estudiante', emoji: '👨‍🎓' },
  { id: 'maestro', label: '👩‍🏫 Maestro', emoji: '👩‍🏫' },
  { id: 'empresa', label: '🏢 Empresa', emoji: '🏢' },
];

const features = {
  estudiante: [
    { icon: '📊', title: 'Calificaciones en Tiempo Real', desc: 'Consulta tus notas por período y actividad, con historial completo de niveles cursados.' },
    { icon: '📅', title: 'Mi Horario', desc: 'Visualiza tus días y horas de clase. Se actualiza automáticamente si la administración hace cambios.' },
    { icon: '✅', title: 'Registro de Asistencias', desc: 'Ve tu historial de puntualidad: Presente, Falta o Retardo, con impacto en tu calificación.' },
    { icon: '💳', title: 'Mis Pagos', desc: 'Consulta tu estado de cuenta, descarga o imprime recibos y recibe recordatorios automáticos.' },
    { icon: '🎓', title: 'Kárdex Académico', desc: 'Tu historial académico oficial descargable en PDF como comprobante de estudios por nivel completado.' },
    { icon: '📚', title: 'Recursos y Biblioteca', desc: 'Accede a materiales didácticos, audios, videos y solicita préstamos de libros desde el sistema.' },
    { icon: '💬', title: 'Mensajería Interna', desc: 'Comunícate directamente con tu maestro o con administración de forma segura.' },
    { icon: '🏆', title: 'Misiones y Gamificación', desc: 'Gana puntos XP, sube en el Leaderboard y completa misiones para hacer el aprendizaje más dinámico.' },
    { icon: '🆔', title: 'Credencial Digital', desc: 'Descarga tu identificación oficial digital desde tu perfil de estudiante.' },
    { icon: '⭐', title: 'Encuesta de Maestros', desc: 'Evalúa a tu maestro de forma confidencial al finalizar el período.' },
    { icon: '📣', title: 'Quejas y Sugerencias', desc: 'Canal directo y confidencial para reportar situaciones o compartir ideas a coordinación.' },
    { icon: '🌐', title: 'LinguaSkills LMS', desc: 'Plataforma de aprendizaje online complementaria con lecciones interactivas, evaluaciones y certificados.' },
  ],
  maestro: [
    { icon: '📋', title: 'Pasar Lista', desc: 'Registra asistencia de cada estudiante (Presente / Falta / Retardo) con fecha y hora automática. Los alumnos que asisten ganan XP.' },
    { icon: '✏️', title: 'Evaluar Estudiantes', desc: 'Ingresa calificaciones por actividad y período. Visibles para el alumno inmediatamente al guardar.' },
    { icon: '📆', title: 'Dosificación', desc: 'Consulta tu plan de trabajo por período con unidades y temas asignados al grupo.' },
    { icon: '👥', title: 'Gestión de Grupos', desc: 'Ve tus grupos activos, lista de alumnos, expediente de cada estudiante y flujo de avance del grupo.' },
    { icon: '📝', title: 'Reportes de Clase', desc: 'Crea reportes con tema cubierto, observaciones y actividades realizadas, visibles para coordinación.' },
    { icon: '🗂️', title: 'Planes de Clase', desc: 'Crea y guarda tu planeación didáctica. El repositorio guarda todos tus planes anteriores para reutilizarlos.' },
    { icon: '🎓', title: 'Liberar Kárdex', desc: 'Al finalizar un grupo, revisa y libera el kárdex para que los estudiantes puedan descargarlo.' },
    { icon: '🗓️', title: 'Mi Horario', desc: 'Consulta y descarga tu horario completo del período actual.' },
    { icon: '💬', title: 'Mensajería y Mensajes Grupales', desc: 'Comunícate con estudiantes o administración. Envía avisos masivos a todos los alumnos de un grupo a la vez.' },
    { icon: '🎯', title: 'Crear Misiones', desc: 'Asigna actividades gamificadas con puntos XP y fecha límite, y revisa las entregas de tus estudiantes.' },
    { icon: '📊', title: 'Leaderboard', desc: 'Visualiza la tabla de posiciones de tus estudiantes por XP. Úsala como herramienta de motivación.' },
    { icon: '🎁', title: 'Portal de Beneficios', desc: 'Accede a descuentos, programas de bienestar y reconocimientos institucionales exclusivos para maestros.' },
  ],
  empresa: [
    { icon: '📈', title: 'Dashboard Ejecutivo', desc: 'Resumen de tus grupos activos y número de empleados inscritos en cada uno.' },
    { icon: '👤', title: 'Información de Empleados', desc: 'Listado de empleados inscritos, información académica detallada y filtrado por grupo.' },
    { icon: '📊', title: 'Calificaciones', desc: 'Consulta promedio y notas individuales de tus empleados. Se actualizan en tiempo real.' },
    { icon: '✅', title: 'Control de Asistencias', desc: 'Registro de asistencia por clase y porcentaje de asistencia de cada empleado inscrito.' },
    { icon: '📄', title: 'Reportes PDF', desc: 'Genera y descarga reportes de avance con calificaciones, asistencias y progreso para RRHH o dirección.' },
  ],
};

const stats = [
  { value: '4', label: 'Tipos de usuarios', sub: 'Admin · Maestro · Alumno · Empresa' },
  { value: '400+', label: 'Funciones en el sistema', sub: 'Módulos PHP especializados' },
  { value: '100%', label: 'En la nube', sub: 'Accede desde cualquier dispositivo' },
  { value: 'PWA', label: 'Instálalo en tu celular', sub: 'Como app nativa' },
];

function LinguaHub() {
  const [activeTab, setActiveTab] = useState('estudiante');

  return (
    <div className="page-wrapper">
      <Helmet>
        <title>LinguaHub | Campus Virtual — Culturlingua</title>
        <meta name="description" content="LinguaHub es el campus virtual de Culturlingua. Gestiona clases, calificaciones, pagos, asistencias, misiones y más desde cualquier dispositivo." />
      </Helmet>

      {/* Hero */}
      <div className="page-hero" style={{ paddingBottom: '80px' }}>
        <div className="section-tag">Campus Virtual</div>
        <h1>LinguaHub</h1>
        <p>La plataforma digital que centraliza toda tu vida académica en Culturlingua.</p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '32px' }}>
          <a
            href="https://linguahub.culturlingua.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-glow"
          >
            Ingresar a LinguaHub →
          </a>
          <a
            href="https://linguaskills.culturlingua.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline"
          >
            Ir a LinguaSkills
          </a>
        </div>
      </div>

      {/* Stats */}
      <section style={{ background: 'var(--navy)', padding: '60px 48px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '40px', textAlign: 'center' }}>
          {stats.map((s, i) => (
            <div key={i}>
              <div style={{ fontSize: '2.8rem', fontWeight: '900', color: 'white', fontFamily: 'Fraunces, serif' }}>{s.value}</div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontWeight: '700', marginTop: '6px' }}>{s.label}</div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', marginTop: '4px' }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Qué es LinguaHub */}
      <section style={{ background: 'var(--cream)', padding: '80px 48px' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', textAlign: 'center' }}>
          <div className="section-tag">¿Qué es?</div>
          <h2 className="section-headline">Tu ecosistema académico completo</h2>
          <p className="section-sub" style={{ margin: '0 auto' }}>
            LinguaHub es el campus virtual de Culturlingua. Desde aquí estudiantes, maestros, coordinación y empresas gestionan todo: calificaciones, asistencias, pagos, comunicación, gamificación y mucho más. Accesible desde computadora, tablet o smartphone — incluso se puede instalar como app.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px', marginTop: '56px' }}>
            {[
              { icon: '🔐', title: 'Acceso por rol', desc: 'Cada usuario ve exactamente lo que necesita según su perfil.' },
              { icon: '📱', title: 'Responsive + PWA', desc: 'Funciona en cualquier dispositivo. Instálalo en tu pantalla de inicio.' },
              { icon: '🔄', title: 'Tiempo real', desc: 'Calificaciones, asistencias y mensajes se actualizan al instante.' },
              { icon: '🤖', title: 'Inteligencia Artificial', desc: 'ChatBot inteligente para todos los usuarios. Panel de predicción de datos integrados.' },
            ].map((item, i) => (
              <div key={i} className="modal-card" style={{ textAlign: 'left' }}>
                <div className="modal-card-accent"></div>
                <div className="modal-card-body">
                  <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{item.icon}</div>
                  <h3 style={{ marginBottom: '8px' }}>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features por rol */}
      <section style={{ background: 'white', padding: '80px 48px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag">Funciones por perfil</div>
            <h2 className="section-headline">¿Qué puedes hacer en LinguaHub?</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>Selecciona tu tipo de usuario para explorar todo lo que tienes disponible.</p>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '48px', flexWrap: 'wrap' }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 28px',
                  borderRadius: '100px',
                  border: '2px solid',
                  borderColor: activeTab === tab.id ? 'var(--blue)' : 'rgba(0,0,0,0.1)',
                  background: activeTab === tab.id ? 'var(--blue)' : 'white',
                  color: activeTab === tab.id ? 'white' : 'var(--dark)',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Feature Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
            {features[activeTab].map((feat, i) => (
              <div key={i} className="modal-card">
                <div className="modal-card-accent"></div>
                <div className="modal-card-body">
                  <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{feat.icon}</div>
                  <h3 style={{ marginBottom: '8px' }}>{feat.title}</h3>
                  <p>{feat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LinguaSkills LMS */}
      <section style={{ background: 'var(--cream)', padding: '80px 48px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto', display: 'flex', gap: '60px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '280px' }}>
            <div className="section-tag">Plataforma LMS</div>
            <h2 className="section-headline" style={{ fontSize: '2rem' }}>LinguaSkills</h2>
            <p style={{ color: 'var(--muted)', lineHeight: '1.8', marginBottom: '24px' }}>
              Tu complemento de aprendizaje online. Accede a cursos interactivos, lecciones con texto, imágenes y ejercicios, evaluaciones en línea y obtén certificados al completar cada curso.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {['Mismas credenciales que LinguaHub', 'Progreso sincronizado automáticamente', 'Lecciones interactivas y evaluaciones', 'Certificados descargables en PDF', 'Comentarios por lección con tu maestro'].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--muted)' }}>
                  <span style={{ color: 'var(--blue)', fontWeight: '700' }}>✓</span> {item}
                </li>
              ))}
            </ul>
            <a href="https://linguaskills.culturlingua.com" target="_blank" rel="noopener noreferrer" className="btn-glow">
              Ir a LinguaSkills →
            </a>
          </div>
          <div style={{ flex: '1', minWidth: '260px', background: 'var(--navy)', borderRadius: '24px', padding: '40px', color: 'white' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🌐</div>
            <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.5rem', marginBottom: '12px' }}>linguaskills.culturlingua.com</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.7', marginBottom: '24px', fontSize: '0.95rem' }}>
              Accede con tu usuario y contraseña de LinguaHub. Tu progreso se guarda automáticamente.
            </p>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { label: 'Cursos activos', val: 'Según tu nivel' },
                { label: 'Evaluaciones', val: 'Online interactivas' },
                { label: 'Certificados', val: 'PDF descargable' },
              ].map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>{r.label}</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>{r.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Acceso */}
      <section style={{ background: 'var(--navy)', padding: '80px 48px', textAlign: 'center' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '2.5rem', color: 'white', fontWeight: '900', marginBottom: '16px' }}>
            ¿Listo para ingresar?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '32px', fontSize: '1.1rem' }}>
            Si ya eres alumno o maestro de Culturlingua, accede con tus credenciales institucionales.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '32px' }}>
            <a href="https://linguahub.culturlingua.com" target="_blank" rel="noopener noreferrer" className="btn-glow">
              Ingresar a LinguaHub →
            </a>
            <a href="/contacto" className="btn-outline">
              ¿Necesitas ayuda? Contáctanos
            </a>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem' }}>
            Compatible con Chrome, Firefox y Edge · Disponible como PWA en móvil
          </p>
        </div>
      </section>
    </div>
  );
}

export default LinguaHub;